/**
 * dataLayer adapter — wraps window.dataLayer.push and replays the history that
 * was pushed before the SDK loaded.
 *
 * This is the only adapter that can recover pre-init events. On GTM-only sites
 * it sees the *trigger inputs* (gtm.click, custom pushes), not GA4's own output;
 * see docs/ga-bridge-feasibility.md §3 for what that does and does not cover.
 */

/** GTM internals that are pure plumbing — forwarding them is noise. */
const SKIPPED_EVENTS = new Set([
  'gtm.js', 'gtm.init', 'gtm.init_consent', 'gtm.dom', 'gtm.load',
  'gtm.scrollDepth', 'gtm.triggerGroup'
]);

/**
 * Turn one dataLayer item into the adapter's raw shape.
 * @param {any} item
 * @returns {{name: string, params: object, userProps: object}|null}
 */
export function readDataLayerItem(item) {
  if (!item || typeof item !== 'object') return null;

  // gtag() pushes an `arguments` object: ['event', name, params] / ['config', id, …]
  const isArgs = Object.prototype.toString.call(item) === '[object Arguments]';
  if (isArgs || Array.isArray(item)) {
    const args = Array.prototype.slice.call(item);
    if (args[0] !== 'event' || typeof args[1] !== 'string') return null;
    return { name: args[1], params: flatten(args[2] || {}), userProps: {} };
  }

  const name = item.event;
  if (typeof name !== 'string' || !name) return null;
  if (SKIPPED_EVENTS.has(name)) return null;

  const params = flatten(item, new Set(['event', 'eventCallback', 'eventTimeout', 'gtm.uniqueEventId']));
  return { name, params, userProps: {} };
}

/**
 * Flatten one level of nesting and drop non-serializable values (DOM nodes on
 * gtm.click pushes would otherwise blow up the payload).
 * @param {object} obj
 * @param {Set<string>} [skip]
 * @returns {object}
 */
function flatten(obj, skip) {
  const out = {};
  if (!obj || typeof obj !== 'object') return out;
  Object.keys(obj).forEach((key) => {
    if (skip && skip.has(key)) return;
    const value = obj[key];
    if (value === undefined || value === null || typeof value === 'function') return;
    const flatKey = key.replace(/\./g, '_');
    if (typeof value === 'object') {
      if (typeof Node !== 'undefined' && value instanceof Node) return;
      if (Array.isArray(value)) {
        // ecommerce.items and friends — keep as-is, BulkAPI flattens arrays.
        out[flatKey] = value;
        return;
      }
      Object.keys(value).forEach((nk) => {
        const nv = value[nk];
        if (nv === undefined || nv === null || typeof nv === 'object' || typeof nv === 'function') return;
        out[flatKey + '_' + nk.replace(/\./g, '_')] = nv;
      });
      return;
    }
    out[flatKey] = value;
  });
  return out;
}

/**
 * @param {object} ctx { emit(raw, 'datalayer'), log(...), win }
 * @returns {{ name: string, uninstall: Function }|null} null when unavailable
 */
export function installDataLayerAdapter(ctx) {
  const win = ctx.win;
  if (!win) return null;

  win.dataLayer = win.dataLayer || [];
  const dl = win.dataLayer;

  // Replay history first so events pushed before SDK init are not lost.
  const history = Array.prototype.slice.call(dl);
  history.forEach((item) => ctx.emit(safeRead(item, ctx), 'datalayer'));
  ctx.log(`[B2M GA bridge] dataLayer history replayed (${history.length} items)`);

  const apply = () => {
    if (dl.push && dl.push.__b2m_ga_bridge) return;
    const originalPush = dl.push;
    const patched = function (...args) {
      // Deliver to GA first, and never let our own failure surface to the
      // caller: this runs inside GTM's push path.
      const result = originalPush.apply(dl, args);
      args.forEach((item) => ctx.emit(safeRead(item, ctx), 'datalayer'));
      return result;
    };
    patched.__b2m_ga_bridge = true;
    patched.__b2m_original = originalPush;
    dl.push = patched;
  };

  apply();

  // GTM can replace push during its own bootstrap; re-apply for the first 5s.
  let attempts = 0;
  const guard = setInterval(() => {
    apply();
    if (++attempts >= 10) clearInterval(guard);
  }, 500);

  return {
    name: 'datalayer',
    uninstall() {
      clearInterval(guard);
      if (dl.push && dl.push.__b2m_ga_bridge) dl.push = dl.push.__b2m_original;
    }
  };
}

/**
 * @param {any} item
 * @param {object} ctx
 * @returns {object|null}
 */
function safeRead(item, ctx) {
  try {
    return readDataLayerItem(item);
  } catch (e) {
    ctx.log('[B2M GA bridge] dataLayer item skipped', e);
    return null;
  }
}
